import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePerformanceMetrics } from '@/hooks/usePerformanceMetrics';

/**
 * Hook Testing Hub
 * 
 * A central navigation page for all the React hook testing tools
 * and utilities we've developed to validate our hook refactoring.
 */
export default function HookTestingHub() {
  // Use performance metrics to measure component performance
  const { measureRender, markInteractive } = usePerformanceMetrics('HookTestingHub');
  const endRenderMeasure = measureRender();
  
  // Tools to be showcased
  const tools = [
    {
      name: 'Hook Execution Tester',
      path: '/test-hooks',
      description: 'Basic tool to test hook execution across different states',
      icon: '🧪',
      color: 'bg-blue-100 text-blue-800',
      buttonColor: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      name: 'Hook Testing Dashboard',
      path: '/hook-testing',
      description: 'Comprehensive dashboard with advanced hook visualization tools',
      icon: '📊',
      color: 'bg-purple-100 text-purple-800',
      buttonColor: 'bg-purple-600 hover:bg-purple-700'
    },
    {
      name: 'Index Component Test',
      path: '/index-test',
      description: 'Specialized testing environment for the refactored Index component',
      icon: '📱',
      color: 'bg-green-100 text-green-800',
      buttonColor: 'bg-green-600 hover:bg-green-700'
    }
  ];
  
  // Mark component as interactive
  markInteractive();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>React Hook Testing Hub | Meduler</title>
        <meta name="description" content="Central hub for hook testing tools and utilities" />
      </Helmet>
      
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            React Hook Testing Hub
          </h1>
          <p className="mt-3 text-xl text-gray-500 max-w-3xl mx-auto">
            A collection of specialized tools for testing and validating React hook execution patterns
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {tools.map((tool) => (
            <div 
              key={tool.path}
              className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col"
            >
              <div className={`w-12 h-12 ${tool.color} rounded-full flex items-center justify-center text-2xl mb-4`}>
                {tool.icon}
              </div>
              
              <h2 className="text-xl font-bold mb-2">{tool.name}</h2>
              <p className="text-gray-600 mb-6 flex-grow">{tool.description}</p>
              
              <Link
                to={tool.path}
                className={`px-4 py-2 ${tool.buttonColor} text-white rounded-md text-center`}
              >
                Open Tool
              </Link>
            </div>
          ))}
        </div>
        
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6">Hook Execution Validation Framework</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold mb-3">Key Principles</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Hooks must execute in the same order on every render</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Avoid early returns before all hooks have executed</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Use conditional rendering instead of conditional hook execution</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Maintain consistent component tree structure across renders</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Avoid nesting hooks inside IIFEs or other non-standard patterns</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Testing Approach</h3>
              <ol className="space-y-2 list-decimal ml-5 text-gray-700">
                <li>Visual inspection of hook execution using analyzer tools</li>
                <li>Automated verification with hook testing utilities</li>
                <li>Performance measurement to quantify improvements</li>
                <li>Cross-state validation to ensure consistency</li>
                <li>Documentation of patterns and anti-patterns</li>
              </ol>
            </div>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-amber-800 mb-2">Why This Matters</h3>
            <p className="text-amber-700">
              React's hook execution rules are fundamental to stable component behavior. 
              Inconsistent hook execution can lead to hard-to-debug errors, state management issues,
              memory leaks, and poor performance. Our testing tools help ensure these rules 
              are followed consistently across the codebase.
            </p>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <Link 
            to="/"
            className="inline-flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
          >
            ← Back to Homepage
          </Link>
        </div>
      </div>
      
      {/* End render measurement at the end of the component */}
      {endRenderMeasure()}
    </div>
  );
}
